import { useEffect, useState } from 'react';
import {
  DEFAULT_MERCHANT_FORM,
  MERCHANT_CODE_OPTIONS,
  formToPayload,
  rowToForm,
} from './merchantConstants';
import { FALLBACK_CURRENCY_OPTIONS } from './paymentAssetConstants';

export default function MerchantEditModal({
  open,
  row,
  currencyOptions,
  onClose,
  onSubmit,
  submitting,
}) {
  const isEdit = Boolean(row?.id);
  const [form, setForm] = useState(DEFAULT_MERCHANT_FORM);
  const [error, setError] = useState('');

  const options = currencyOptions?.length ? currencyOptions : FALLBACK_CURRENCY_OPTIONS;

  useEffect(() => {
    if (open) {
      const nextForm = rowToForm(row);
      if (!nextForm.currencyType && options.length) {
        nextForm.currencyType = options[0].value;
      }
      setForm(nextForm);
      setError('');
    }
  }, [open, row?.id, currencyOptions]);

  if (!open) {
    return null;
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form.name?.trim()) {
      setError('請填寫商戶名稱');
      return;
    }
    if (!form.merchantCode?.trim()) {
      setError('請填寫商戶 Code');
      return;
    }
    if (!form.merchantId?.trim()) {
      setError('請填寫商戶 ID');
      return;
    }
    if (!form.currencyType?.trim()) {
      setError('請選擇貨幣類型');
      return;
    }
    setError('');
    await onSubmit(formToPayload(form, row?.id));
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>{isEdit ? '編輯線上商戶' : '新增線上商戶'}</h3>
          <button type="button" className="admin-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal__body">
            <section className="settings-section">
              <h4>基本資料</h4>
              <div className="form-grid">
                <div className="form-field">
                  <label htmlFor="merchantName">商戶名稱</label>
                  <input
                    id="merchantName"
                    type="text"
                    value={form.name}
                    onChange={(e) => updateField('name', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="merchantCode">商戶 Code</label>
                  <input
                    id="merchantCode"
                    type="text"
                    list="merchantCodeOptions"
                    value={form.merchantCode}
                    disabled={isEdit}
                    onChange={(e) => updateField('merchantCode', e.target.value)}
                    placeholder="如 aawalletpayV2"
                  />
                  <datalist id="merchantCodeOptions">
                    {MERCHANT_CODE_OPTIONS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </datalist>
                </div>
                <div className="form-field">
                  <label htmlFor="merchantId">商戶 ID</label>
                  <input
                    id="merchantId"
                    type="text"
                    value={form.merchantId}
                    onChange={(e) => updateField('merchantId', e.target.value)}
                    placeholder="三方商戶號"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="merchantCurrencyType">貨幣類型</label>
                  <select
                    id="merchantCurrencyType"
                    value={form.currencyType}
                    onChange={(e) => updateField('currencyType', e.target.value)}
                  >
                    {options.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <p className="form-hint">選項來自幣種管理中的幣別設定</p>
                </div>
                <div className="form-field">
                  <label htmlFor="merchantSort">排序</label>
                  <input
                    id="merchantSort"
                    type="number"
                    value={form.sort}
                    onChange={(e) => updateField('sort', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="merchantImage">圖示 URL</label>
                  <input
                    id="merchantImage"
                    type="text"
                    value={form.image}
                    onChange={(e) => updateField('image', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="merchantDepositLang">充值訂單語言</label>
                  <input
                    id="merchantDepositLang"
                    type="text"
                    value={form.depositOrderLanguage}
                    onChange={(e) => updateField('depositOrderLanguage', e.target.value)}
                    placeholder="可留空"
                  />
                </div>
              </div>
            </section>

            <section className="settings-section">
              <h4>接口配置</h4>
              <div className="form-grid">
                <div className="form-field">
                  <label htmlFor="merchantUrl">商戶域名</label>
                  <input
                    id="merchantUrl"
                    type="text"
                    value={form.url}
                    onChange={(e) => updateField('url', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="merchantGateway">網關</label>
                  <input
                    id="merchantGateway"
                    type="text"
                    value={form.gateway}
                    onChange={(e) => updateField('gateway', e.target.value)}
                  />
                </div>
                <div className="form-field form-field--full">
                  <label htmlFor="merchantOrderUrl">創建收款地址</label>
                  <input
                    id="merchantOrderUrl"
                    type="text"
                    value={form.orderUrl}
                    onChange={(e) => updateField('orderUrl', e.target.value)}
                  />
                </div>
                <div className="form-field form-field--full">
                  <label htmlFor="merchantOrderQueryUrl">訂單查詢地址</label>
                  <input
                    id="merchantOrderQueryUrl"
                    type="text"
                    value={form.orderQueryUrl}
                    onChange={(e) => updateField('orderQueryUrl', e.target.value)}
                  />
                </div>
                <div className="form-field form-field--full">
                  <label htmlFor="merchantWithdrawalUrl">創建代付地址</label>
                  <input
                    id="merchantWithdrawalUrl"
                    type="text"
                    value={form.withdrawalUrl}
                    onChange={(e) => updateField('withdrawalUrl', e.target.value)}
                  />
                </div>
                <div className="form-field form-field--full">
                  <label htmlFor="merchantWithdrawalQueryUrl">代付查詢地址</label>
                  <input
                    id="merchantWithdrawalQueryUrl"
                    type="text"
                    value={form.withdrawalQueryUrl}
                    onChange={(e) => updateField('withdrawalQueryUrl', e.target.value)}
                  />
                </div>
                <div className="form-field form-field--full">
                  <label htmlFor="merchantBalanceUrl">餘額查詢地址</label>
                  <input
                    id="merchantBalanceUrl"
                    type="text"
                    value={form.balanceAmountUrl}
                    onChange={(e) => updateField('balanceAmountUrl', e.target.value)}
                  />
                </div>
                <div className="form-field form-field--full">
                  <label htmlFor="merchantNotifyUrl">異步通知地址</label>
                  <input
                    id="merchantNotifyUrl"
                    type="text"
                    value={form.notifyUrl}
                    onChange={(e) => updateField('notifyUrl', e.target.value)}
                  />
                </div>
              </div>
            </section>

            <section className="settings-section">
              <h4>密鑰</h4>
              <div className="form-grid">
                <div className="form-field form-field--full">
                  <label htmlFor="merchantPublicKey">公鑰</label>
                  <textarea
                    id="merchantPublicKey"
                    rows={3}
                    value={form.publicKey}
                    onChange={(e) => updateField('publicKey', e.target.value)}
                  />
                </div>
                <div className="form-field form-field--full">
                  <label htmlFor="merchantSecretKey">私鑰</label>
                  <textarea
                    id="merchantSecretKey"
                    rows={3}
                    value={form.secretKey}
                    onChange={(e) => updateField('secretKey', e.target.value)}
                  />
                </div>
              </div>
            </section>

            {error && <div className="alert alert-error">{error}</div>}
          </div>
          <div className="admin-modal__footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              取消
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? '保存中…' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
